import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import {
  Tab,
  Tabs,
  makeStyles,
  Typography,
  Button,
  TextField,
} from '@material-ui/core';
import {
  CommonDialog,
  NormPriceComposition,
  NormCoefficients,
  EstimatePriceSettings,
  RichTextEditor,
} from 'components';
import api from 'utils/api';

const TABS = {
  PRICE_COMPOSITION: 'price_composition',
  COEFFICIENTS: 'coefficients',
  PRICE_SETTINGS: 'price_settings',
  JOB_COMPOSITION: 'job_composition',
  ELEMENT_PARAMETERS: 'element_parameters',
}

const allowedToEditIfRate = [
  'k9',
  'k8',
  'k11',
  'k21',
  'k31',
  'k51',
];

const allowedToEditIfNotRate = [
  'pdu',
  'sd',
  'pmed',
  'pmech',
  'sti',
  'pr',
  'p',
  'k11',
  'k21',
  'k31',
  'k51',
  'pvm',
];

const useStyles = makeStyles({
  root: {},
  filePickerTableContainer: {
    maxWidth: 800,
  },
  footerWrap: {
    marginTop: 40,
  }
})

const ItemSummaryModal = ({ objectId, itemId, onClose, onChange, initialTab = TABS.PRICE_COMPOSITION, readOnly = false, isEstimate = true }) => {
  const history = useHistory();
  const classes = useStyles();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState(null);
  const [title, setTitle] = useState(null);
  const [isRate, setIsRate] = useState(false);
  const [hadChanges, setHadChanges] = useState(false);
  const [priceSettings, setPriceSettings] = useState(null);
  const [coefficients, setCoefficients] = useState(null);
  const [workComposition, setWorkComposition] = useState();
  const [mapping, setMapping] = useState([]);
  const [isElement, setIsElement] = useState(false);
  const [isResource, setIsResource] = useState(false);

  // If work report data - only allow read-only mode
  if (!isEstimate) readOnly = true;

  const fetchItem = async () => {
    setLoading(true);

    try {
      const { data } = isEstimate ? await api.get(`/estimates/${objectId}/items/${itemId}/title-summary`) : await api.get(`/work-reports/${objectId}/items/${itemId}/title-summary`);
      setItem({ ...data });
      setIsRate(data.type === 'rate');
      setIsElement(['element', 'group'].includes(data.type));
      setIsResource(data.type === 'resource');
      setTitle(<><b>{data.code}</b> {data.title}</>);

      const { data: activeTabData } = activeTab != 'element_parameters' ? 
        isEstimate 
          ? await api.get(`/estimates/${objectId}/items/${itemId}/partial-summary/${activeTab}`) 
          : await api.get(`/work-reports/${objectId}/items/${itemId}/partial-summary/${activeTab}`) 
        : {};
      if (activeTab === TABS.JOB_COMPOSITION) {
        setWorkComposition(activeTabData?.workComposition);
      }
      if (activeTab === TABS.PRICE_COMPOSITION) {
        setItem({ ...data, totals: activeTabData.totals });
      }
      if (activeTab === TABS.COEFFICIENTS) {
        const coefficients = activeTabData?.coefficients ? activeTabData.coefficients : [];
        const coefficientsSpec = activeTabData?.coefficientsSpec ? activeTabData.coefficientsSpec : [];
        const customCoefficients = activeTabData?.customCoefficients ? activeTabData.customCoefficients : [];

        setCoefficients({ coefficients, coefficientsSpec, customCoefficients });
        setItem({ ...data, coefficients, coefficientsSpec, customCoefficients });
      }
      if (activeTab === TABS.PRICE_SETTINGS) {
        setPriceSettings(activeTabData.priceSettings);
        setItem({ ...data, priceSettings: activeTabData.priceSettings });
      }
      if (activeTab === TABS.ELEMENT_PARAMETERS) {
        if (!mapping.length) {
          const fetchMapping = async () => {
            try {
              const { data } = await api.get(`/estimates/${objectId}/items/${itemId}/mapping`);

              setMapping(data);
            } catch (err) {
              console.error(err);
            }
          };

          await fetchMapping();
        }

      }
    } catch (err) {
      console.error(err);
    }

    setLoading(false);
  }

  useEffect(() => {
    if (!item) fetchItem();
  }, []);

  const handleClose = () => {
    onClose(hadChanges);
  };

  const handleTabChange = async (event, tabCode) => {
    const request = isEstimate ? `/estimates/${objectId}/items/${itemId}/partial-summary/${tabCode}` : `/work-reports/${objectId}/items/${itemId}/partial-summary/${tabCode}`;

    if (tabCode === TABS.PRICE_SETTINGS && !item.priceSettings) {
      const { data } = await api.get(request);
      setPriceSettings(data.priceSettings);
      setItem({ ...item, priceSettings: data.priceSettings });
    }
    if (tabCode === TABS.COEFFICIENTS && !item.coefficients && !item.coefficientsSpec && !item.customCoefficients) {
      const { data } = await api.get(request);
      const coefficients = data?.coefficients ? data.coefficients : [];
      const coefficientsSpec = data?.coefficientsSpec ? data.coefficientsSpec : [];
      const customCoefficients = data?.customCoefficients ? data.customCoefficients : [];

      setCoefficients({ coefficients, coefficientsSpec, customCoefficients });
      setItem({ ...item, coefficients, coefficientsSpec, customCoefficients });
    }
    if (tabCode === TABS.JOB_COMPOSITION && !item.workComposition) {
      const { data } = await api.get(request);
      setWorkComposition(data?.workComposition);
      setItem({ ...item, workComposition: data?.workComposition });
    }
    if (tabCode === TABS.PRICE_COMPOSITION && !item.totals) {
      const { data } = await api.get(request);
      setItem({ ...item, totals: data.totals });
    }
    if (tabCode === TABS.ELEMENT_PARAMETERS) {
      if (!mapping.length) {
        const fetchMapping = async () => {
          try {
            const { data } = await api.get(`/estimates/${objectId}/items/${itemId}/mapping`);

            setMapping(data);
          } catch (err) {
            console.error(err);
          }
        };

        await fetchMapping();
      }
    }
    setActiveTab(tabCode);
    return;
  };

  const onPriceSettingsChange = async (priceSettings) => {
    setPriceSettings([...priceSettings]);
  };

  const onCoefficientsChange = async ({
    coefficients = [],
    coefficientsSpec = [],
    customCoefficients = [],
  }) => {
    const coeffs = {
      coefficients,
      coefficientsSpec,
      customCoefficients,
    };

    setCoefficients(coeffs);
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (activeTab === TABS.PRICE_SETTINGS) {
      try {
        await api.put(`/estimates/${objectId}/items/${itemId}/price-settings`, priceSettings);

        setHadChanges(true);

        await fetchItem();
      } catch (err) {
        console.error(err);
      }
    } else if (activeTab === TABS.COEFFICIENTS) {
      try {
        await api.put(`/estimates/${objectId}/items/${itemId}/coefficients`, coefficients);

        setHadChanges(true);

        await fetchItem();
      } catch (err) {
        console.error(err);
      }
    } else if (activeTab === TABS.JOB_COMPOSITION) {
      try {
        await api.put(`/estimates/${objectId}/items/${itemId}`, {
          value: workComposition,
          field: 'workComposition',
          onlyField: true,
        });

        setHadChanges(true);

        await fetchItem();
      } catch (err) {
        console.error(err);
      }
    } else {
      window.alert('Unknown type ' + activeTab);
    }

    setLoading(false);
  }

  const actions = (
    <>
      {[TABS.COEFFICIENTS, TABS.PRICE_SETTINGS, TABS.JOB_COMPOSITION].includes(activeTab) && !readOnly && (
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleSubmit()}
        >
          {activeTab === TABS.COEFFICIENTS && t('Perskaičiuoti su koeficientais')}
          {activeTab === TABS.PRICE_SETTINGS && t('Perskaičiuoti su kainos nustatymais')}
          {activeTab === TABS.JOB_COMPOSITION && t('Išsaugoti darbų sudėtį')}
        </Button>
      )}

      <Button
        color="primary"
        onClick={handleClose}
      >
        Uždaryti
      </Button>
    </>
  );

  return (
    <CommonDialog
      visible
      onClose={handleClose}
      maxWidth="md"
      style={{ position: 'relative' }}
      showClose
      actions={actions}
      disableBackdropClick
    >
      {title && (
        <Typography variant="body1">
          {title}
        </Typography>
      )}

      <Tabs
        value={activeTab}
        indicatorColor="primary"
        textColor="primary"
        onChange={handleTabChange}
      >
        {!isResource && <Tab label={t("Kainos sudėtis")} value={TABS.PRICE_COMPOSITION} />}
        <Tab label={!isResource && !isElement ? t("Darbų sudėtis") : t("Charakteristika")} value={TABS.JOB_COMPOSITION} />
        {isRate && <Tab label={t("Koeficientai")} value={TABS.COEFFICIENTS} />}
        {!isResource && <Tab label={t("Darbo kainos nustatymai")} value={TABS.PRICE_SETTINGS} />}
        {isElement && <Tab label={t("Papildoma informacija")} value={TABS.ELEMENT_PARAMETERS} />}
      </Tabs>

      <div style={{ minHeight: '450px', marginTop: '10px' }}>
        {activeTab === TABS.PRICE_COMPOSITION && <NormPriceComposition hideAdditionals={!isRate} item={item} />}
        {activeTab === TABS.JOB_COMPOSITION && (
          <>
            {readOnly ? (
              <div dangerouslySetInnerHTML={{ __html: workComposition || t('Nėra duomenų') }}></div>
            ) : (
              <RichTextEditor
                placeholder={`${t('Įveskite darbų sudėties aprašymą')}...`}
                value={workComposition}
                onChange={(html) => setWorkComposition(html)}
              />
            )}
          </>
        )}
        {activeTab === TABS.COEFFICIENTS && isRate && <NormCoefficients readOnly={readOnly} onChange={onCoefficientsChange} item={item} />}
        {activeTab === TABS.PRICE_SETTINGS && <EstimatePriceSettings readOnly={readOnly} isRate={isRate} onChange={onPriceSettingsChange} priceSettings={priceSettings} />}
        {activeTab === TABS.ELEMENT_PARAMETERS && !!mapping.length && (
          <div style={{ padding: '5px 10px' }}>
            {mapping.map((m) => (
              <div style={{ width: '20%', display: 'inline-block', marginBottom: 5, verticalAlign: 'top' }}>
                <small>
                  <strong>{m.key} {m.title}:</strong><br />{m.value}
                </small>
              </div>
            ))}
          </div>
        )}
      </div>
    </CommonDialog>
  )
}

export default ItemSummaryModal;